import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import RepeaterEditor from '../components/RepeaterEditor';
import ImageUploader from '../components/ImageUploader';

const defaults = {
  sectionId: "ÜberDasFestival",
  title: "Über das Festival",
  imageUrl: "/img/about/about.jpg",
  greetingTitle: "Grußwort",
  paragraphs: []
};

export default function AboutEditor() {
  const api = useAdminApi();
  const [doc, setDoc] = useState(defaults);
  const [orig, setOrig] = useState(defaults);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dirty = useMemo(()=>JSON.stringify(doc)!==JSON.stringify(orig), [doc,orig]);

  useEffect(()=>{ let alive=true;(async()=>{
    setLoading(true);
    try{
      const res = await api.get('/sections/about');
      if(!alive) return;
      const merged = { ...defaults, ...(res||{}) };
      setDoc(merged); setOrig(merged);
    } finally { setLoading(false); }
  })(); return ()=>{alive=false}; },[]); // eslint-disable-line

  const onSave = async () => {
    setSaving(true); setError('');
    try{ await api.put('/sections/about', doc); setOrig(doc); setEditing(false); }
    catch(e){ setError(e?.message || 'Save failed'); }
    finally{ setSaving(false); }
  };

  if (loading) return <Spinner/>;

  return (
    <div>
      <Toolbar title="Über das Festival" editing={editing} setEditing={setEditing} onSave={onSave} onCancel={()=>{setDoc(orig);setEditing(false);}} saving={saving} dirty={dirty}/>
      {error && <div className="alert alert-danger m-3 py-2">{error}</div>}
      <div className="p-3">
        <Row>
          <Col><Text label="Titel" value={doc.title} onChange={(v)=>setDoc(d=>({...d, title:v}))} readOnly={!editing}/></Col>
           <Col><Text label="Grußwort-Titel" value={doc.greetingTitle} onChange={(v)=>setDoc(d=>({...d, greetingTitle:v}))} readOnly={!editing}/></Col>
        </Row>
        <Row>
          <Col>
              <ImageUploader
                label="Bild"
                value={doc.imageUrl}
                onChange={(url)=>setDoc(d=>({...d, imageUrl:url}))}
                folder="about"
                readOnly={!editing}
                help="JPG/PNG, max 8 MB. Empfohlen ca. 900×600."
              />
          </Col>
        </Row>

        <RepeaterEditor
          title="Absätze"
          items={(doc.paragraphs||[]).map(t=>({text:t}))}
          onChange={(list)=>setDoc(d=>({...d, paragraphs: list.map(x=>x.text||'')}))}
          template={{ text: '' }}
          renderFields={(it, patch)=>(
            <Textarea label="Text" value={it.text} onChange={(v)=>patch({text:v})} readOnly={!editing}/>
          )}
        />
      </div>
    </div>
  );
}
function Toolbar(p){ /* same as in Carousel */ return CarouselToolbar(p); }
function Spinner(){ return (<div className="p-4 text-center"><div className="spinner-border brand-spinner" role="status" /><div className="small mt-2 text-muted">Lädt…</div></div>); }
const CarouselToolbar = ({ title, editing, setEditing, onSave, onCancel, saving, dirty }) => (
  <div className="editor-toolbar">
    <div className="d-flex align-items-center gap-2">
      <h4 className="editor-title m-0">{title}</h4>
      {editing && dirty && <span className="badge-soft">• Ungespeicherte Änderungen</span>}
    </div>
    <div className="d-flex align-items-center gap-2">
      {!editing ? <button className="btn btn-sm btn-edit" onClick={()=>setEditing(true)}>Bearbeiten</button> : (
        <>
          <button className="btn btn-sm btn-save" onClick={onSave} disabled={!dirty || saving}>{saving ? 'Speichert…' : 'Speichern'}</button>
          <button className="btn btn-sm btn-cancel" onClick={onCancel} disabled={saving}>Abbrechen</button>
        </>
      )}
    </div>
  </div>
);
function Text({label, value, onChange, readOnly}){ return (<div className="mb-3"><label className="form-label">{label}</label><input className="form-control" value={value||''} onChange={e=>onChange(e.target.value)} readOnly={readOnly}/></div>); }
function Textarea({label, value, onChange, readOnly, rows=4}){ return (<div className="mb-3"><label className="form-label">{label}</label><textarea className="form-control" rows={rows} value={value||''} onChange={e=>onChange(e.target.value)} readOnly={readOnly}/></div>); }
const Row = ({children}) => <div className="row">{children}</div>;
const Col = ({children}) => <div className="col-md-6">{children}</div>;
